// POST /api/admin/override-bulk — atomic multi-key writer (H9 PR-1).
//
// Frontend gebruikt UITSLUITEND de bulk-endpoint zodat alle Settings-changes
// in één locked write naar dashboard-overrides.yaml gaan. trigger_restart=true
// herstart alle zbs-* services (~1s vertraging zodat de response nog terugkomt
// voor de zbs-api zichzelf killt).

import { postJson } from './client';

export type OverrideValue = string | number | boolean;

export interface OverrideChange {
  key: string;
  value: OverrideValue;
}

export interface OverrideBulkRequest {
  changes: OverrideChange[];
  trigger_restart?: boolean;
}

export interface OverrideBulkResponse {
  status: 'written';
  applied: number;
  path: string;
  restart_triggered: boolean;
  note: string;
}

export function submitOverrideBulk(req: OverrideBulkRequest): Promise<OverrideBulkResponse> {
  return postJson<OverrideBulkResponse>('/api/admin/override-bulk', req);
}

// Paniek-knop (Bram-PRIO 2026-05-19): ACTIVE→SAFE flip moet de inverter
// onmiddellijk in standaard Solis self-use zetten — niet wachten op restart +
// 5-min watchdog. Endpoint schrijft signal-file, flipt ems.mode=safe, triggert
// restart; engine init detecteert signal en doet direct SC disengage + 43110
// baseline. Eind-tot-eind ~3s.
export interface EmsSafeNowResponse {
  status: 'scheduled';
  signal_file: string;
  override: string;
  restart_triggered: boolean;
  note: string;
}

export function submitEmsSafeNow(): Promise<EmsSafeNowResponse> {
  return postJson<EmsSafeNowResponse>('/api/admin/ems-safe-now', {});
}

// Tegenhanger van de paniek-knop: van shadow-only naar daadwerkelijk schrijven.
// De sleutel `ems.mode` loopt sinds punt 61 niet meer via de generieke
// override-route — die geeft er HTTP 400 op — omdat de zwaarste
// toestandsovergang van het systeem daar exact dezelfde vorm had als het
// bijstellen van een tijdsdrempel.
//
// De server schrijft de waarde hardgecodeerd; het enige wat de client meegeeft
// is `confirm_site`, de naam van deze site uit `installation.name`. Dat veld
// bestaat om te voorkomen dat een payload die elders is gemaakt deze Pi
// activeert, en wordt serverzijdig hoofdletterongevoelig vergeleken. De client
// valideert daarom niets vooraf en toont bij een mismatch gewoon de
// serverfout.
//
// De herstart is een aparte keuze in de body. Zonder `trigger_restart` landt de
// flip alleen in dashboard-overrides.yaml en blijft de engine in shadow-only
// draaien tot de loop is herstart. De tegel vraagt de herstart daarom mee — dat
// is wat ze vóór punt 61 ook deed via de bulk-route — zodat het klikken van de
// knop en het effect op de omvormer één moment blijven.
//
// Weigert de server de bevestiging (400), dan is er niets geschreven en niets
// herstart.
export interface EmsActivateResponse {
  status: 'written';
  site: string;
  key: 'ems.mode';
  value: 'active';
  path: string;
  restart_triggered: boolean;
  note: string;
}

export function submitEmsActivate(
  confirmSite: string,
  triggerRestart = true,
): Promise<EmsActivateResponse> {
  return postJson<EmsActivateResponse>('/api/admin/ems-activate', {
    confirm_site: confirmSite,
    trigger_restart: triggerRestart,
  });
}
